import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type SearchPdpsV1WorkspacesWorkspaceIdPdpsGetData,
  type SearchPdpsV1WorkspacesWorkspaceIdPdpsGetError,
  type SearchPdpsV1WorkspacesWorkspaceIdPdpsGetResponse,
  searchPdpsV1WorkspacesWorkspaceIdPdpsGet,
} from '../../generated';

export type SearchPdpsQueryVariables = SearchPdpsV1WorkspacesWorkspaceIdPdpsGetData['path'] &
  SearchPdpsV1WorkspacesWorkspaceIdPdpsGetData['query'];

export type SearchPdpsQuery = {
  data: SearchPdpsV1WorkspacesWorkspaceIdPdpsGetResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useSearchPdpsQuery = <TData = SearchPdpsQuery, TError = SearchPdpsV1WorkspacesWorkspaceIdPdpsGetError>(
  {
    workspace_id,
    limit,
    offset,
    sort_by,
    sort_direction,
    brand,
    product_category_id,
    listing_score_level,
    search_item_id,
    search_name,
    search_brand,
    search_product_category,
  }: SearchPdpsQueryVariables,
  options?: Omit<UseQueryOptions<SearchPdpsQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SearchPdpsQuery, TError, TData>({
    queryKey: [
      'SearchPdps',
      {
        workspace_id,
        limit,
        offset,
        sort_by,
        sort_direction,
        brand,
        product_category_id,
        listing_score_level,
        search_item_id,
        search_name,
        search_brand,
        search_product_category,
      },
    ],
    queryFn: async () => {
      const { data, response } = await searchPdpsV1WorkspacesWorkspaceIdPdpsGet({
        path: { workspace_id },
        query: {
          limit,
          offset,
          sort_by,
          sort_direction,
          brand,
          product_category_id,
          listing_score_level,
          search_item_id,
          search_name,
          search_brand,
          search_product_category,
        },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useSearchPdpsQuery.getKey = ({
  workspace_id,
  limit,
  offset,
  sort_by,
  sort_direction,
  brand,
  product_category_id,
  listing_score_level,
  search_item_id,
  search_name,
  search_brand,
  search_product_category,
}: SearchPdpsQueryVariables) => [
  'SearchPdps',
  {
    workspace_id,
    limit,
    offset,
    sort_by,
    sort_direction,
    brand,
    product_category_id,
    listing_score_level,
    search_item_id,
    search_name,
    search_brand,
    search_product_category,
  },
];

useSearchPdpsQuery.fetcher = (
  {
    workspace_id,
    limit,
    offset,
    sort_by,
    sort_direction,
    brand,
    product_category_id,
    listing_score_level,
    search_item_id,
    search_name,
    search_brand,
    search_product_category,
  }: SearchPdpsQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await searchPdpsV1WorkspacesWorkspaceIdPdpsGet({
      path: { workspace_id },
      query: {
        limit,
        offset,
        sort_by,
        sort_direction,
        brand,
        product_category_id,
        listing_score_level,
        search_item_id,
        search_name,
        search_brand,
        search_product_category,
      },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};
